import { useCallback, useEffect, useState } from 'react';
import feedService, { mapPostReactionUsersToLikedUsers } from '@wlb/shared/api/services/feedService';
import {
  getUserFacingErrorMessage,
  UnusableResponseError,
} from '@wlb/shared/utils/apiErrorHandler';
import { normalizeUserGuid } from '@wlb/shared/utils/navigateToUserProfile';
import {
  isFollowRelationshipTarget,
  notifyFollowRelationshipChanged,
  subscribeToFollowRelationshipChanges,
} from '@wlb/shared/utils/followRelationshipEvents';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { useAuth } from '@wlb/shared/context/AuthContext';
import type { LikedUser } from '@wlb/shared/types/post';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { ListStatus } from '../../components/ui/ListStatus';
import { Modal } from '../../components/ui/Modal';
import { avatarFallback } from '../../components/ui/avatarFallback';
import '../../components/ui/avatar.css';
import { useOpenUserProfile } from '../profile/useOpenUserProfile';
import { getUsernameDisplay } from '../../utils/usernameDisplay';
import { FeedUserRowsSkeleton } from './FeedModalSkeleton';
import {
  isLikedUserCurrentUser,
  isLikedUserFollowing,
} from './likesIdentity';
import './feedModal.css';

interface LikesModalProps { postId: string; onClose: () => void; }

export function LikesModal({ postId, onClose }: LikesModalProps) {
  const { t } = useTranslation();
  const { userId: authUserId, userGUID } = useAuth();
  const openUserProfile = useOpenUserProfile();
  const { profile, userRelation } = useProfile();
  const [users, setUsers] = useState<LikedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relationshipOverrides, setRelationshipOverrides] = useState<
    Record<string, boolean | undefined>
  >({});
  const [followLoading, setFollowLoading] = useState<Record<string, boolean>>({});

  const currentUserId = authUserId || profile?.userId || null;
  const currentUserGuid = userGUID ?? profile?.userGuid ?? null;
  const currentUserIdentity = {
    userId: currentUserId,
    userGuid: currentUserGuid,
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await feedService.getPostReactionList(postId);
      // A rejected request is not a post nobody liked.
      if (!result.success) {
        throw new UnusableResponseError('GetPostReactionList');
      }
      setUsers(mapPostReactionUsersToLikedUsers(result.data));
    } catch (err) {
      setError(getUserFacingErrorMessage(err, t('common.errors.generic')));
    } finally {
      setLoading(false);
    }
  }, [postId, t]);

  useEffect(() => { loadUsers().catch(() => undefined); }, [loadUsers]);

  useEffect(() => {
    const subscription = subscribeToFollowRelationshipChanges(change => {
      const matchingUser = users.find(user =>
        isFollowRelationshipTarget(change, {
          userId: user.id,
          profileUserGuid: user.profileUserGuid ?? user.userGuid,
        }),
      );
      if (!matchingUser) return;
      setRelationshipOverrides(current => ({
        ...current,
        [matchingUser.id]: change.isFollowing,
      }));
    });
    return () => subscription.remove();
  }, [users]);

  const toggleFollow = async (user: LikedUser) => {
    const isFollowing = isLikedUserFollowing(
      user,
      relationshipOverrides[user.id],
    );
    if (
      isLikedUserCurrentUser(user, currentUserIdentity) ||
      followLoading[user.id] ||
      !Number.isFinite(Number(user.id))
    ) return;
    const nextFollowing = !isFollowing;
    setRelationshipOverrides(current => ({
      ...current,
      [user.id]: nextFollowing,
    }));
    setFollowLoading(current => ({ ...current, [user.id]: true }));
    try {
      const result = await userRelation(
        Number(user.id),
        nextFollowing ? 'Follow' : 'UnFollow',
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Follow relation update failed');
      }
      const confirmedFollowing =
        typeof result?.isFollowing === 'boolean'
          ? result.isFollowing
          : nextFollowing;
      setRelationshipOverrides(current => ({
        ...current,
        [user.id]: confirmedFollowing,
      }));
      notifyFollowRelationshipChanged({
        relatedUserId: Number(user.id),
        profileUserGuid: user.profileUserGuid ?? user.userGuid,
        isFollowing: confirmedFollowing,
      });
    } catch {
      setRelationshipOverrides(current => ({
        ...current,
        [user.id]: isFollowing,
      }));
      ToastService.show({
        type: 'error',
        message: t('common.errors.generic'),
      });
    } finally {
      setFollowLoading(current => ({ ...current, [user.id]: false }));
    }
  };

  return (
    <Modal title={t('likes.title')} onClose={onClose} labelledBy="likes-modal-title">
      <div className="likes-modal__list" aria-live="polite">
        {loading ? (
          <FeedUserRowsSkeleton />
        ) : error ? (
          <ListStatus variant="error" layout="inline" message={error} onAction={() => { loadUsers().catch(() => undefined); }} />
        ) : users.length === 0 ? (
          <ListStatus variant="empty" layout="inline" title={t('likes.empty')} />
        ) : users.map(user => {
          const userGuid = normalizeUserGuid(user.profileUserGuid) ?? normalizeUserGuid(user.userGuid);
          const displayName = getUsernameDisplay(user);
          const isCurrentUser = isLikedUserCurrentUser(
            user,
            currentUserIdentity,
          );
          const isFollowing = isLikedUserFollowing(
            user,
            relationshipOverrides[user.id],
          );
          const fallback = avatarFallback({ seed: userGuid ?? user.id, name: displayName, username: user.name });
          const identity = (
            <>
              {user.avatar ? (
                <img src={user.avatar} alt="" />
              ) : (
                <span className="likes-modal__avatar" data-avatar-tone={fallback.tone} aria-hidden>
                  {fallback.initial}
                </span>
              )}
              <span>
                <strong>
                  {displayName}
                  {isCurrentUser ? ` ${t('likes.you')}` : ''}
                </strong>
                {user.isBuddy && <small>{t('likes.buddy')}</small>}
                {user.currentWeight && <small>{user.currentWeight}</small>}
              </span>
            </>
          );
          return (
            <div className="likes-modal__user" key={user.id}>
              {userGuid ? (
                <button className="likes-modal__identity" type="button" onClick={() => openUserProfile(userGuid)} aria-label={t('likes.viewProfile', { name: displayName })}>{identity}</button>
              ) : (
                <div className="likes-modal__identity likes-modal__identity--static">{identity}</div>
              )}
              {!isCurrentUser && (
                <button
                  className={`likes-modal__follow${isFollowing ? ' likes-modal__follow--following' : ''}`}
                  type="button"
                  aria-pressed={isFollowing}
                  onClick={() => { toggleFollow(user).catch(() => undefined); }}
                  disabled={followLoading[user.id]}
                >
                  {isFollowing ? t('likes.following') : t('likes.follow')}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
